import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateCell } from '../components/DateCell';
import { showToast } from '../components/ui/toast.utils';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import {
  createImprovementSuggestion,
  escalateImprovementSuggestion,
  fetchImprovementSuggestions,
  updateImprovementSuggestion,
} from '../features/improvement/improvementSuggestionsApi';
import type {
  ImprovementSuggestionStatus,
  ImprovementSuggestionSummary,
} from '../features/improvement/types';

const STATUS_LABEL: Record<ImprovementSuggestionStatus, string> = {
  recibida: 'Recibida',
  en_analisis: 'En análisis',
  implementada: 'Implementada',
  rechazada: 'Rechazada',
};

const STATUS_TRANSITIONS: Record<ImprovementSuggestionStatus, ImprovementSuggestionStatus[]> = {
  recibida: ['en_analisis', 'rechazada'],
  en_analisis: ['implementada', 'rechazada'],
  implementada: [],
  rechazada: [],
};

const getSelectableStatuses = (
  status: ImprovementSuggestionStatus,
): ImprovementSuggestionStatus[] => [status, ...STATUS_TRANSITIONS[status]];

export function ImprovementSuggestionsPage() {
  const { canEdit } = usePlatformAccessLevel('improvement.suggestions.access');
  const [items, setItems] = useState<ImprovementSuggestionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [escalatingId, setEscalatingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchImprovementSuggestions({ pageSize: 50 });
      setItems(result.data);
    } catch {
      setError('No se pudieron cargar las sugerencias de mejora.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!title.trim() || !description.trim()) return;
    setSaving(true);
    try {
      await createImprovementSuggestion({ title, description });
      setTitle('');
      setDescription('');
      await load();
    } catch {
      showToast('No se pudo registrar la sugerencia.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id: string, status: ImprovementSuggestionStatus) => {
    try {
      await updateImprovementSuggestion(id, { status });
      await load();
    } catch {
      showToast('No se pudo actualizar el estado.', 'error');
    }
  };

  const handleEscalate = async (id: string) => {
    setEscalatingId(id);
    try {
      const result = await escalateImprovementSuggestion(id);
      await load();
      showToast(`Escalada a NC: ${result.qmsRecordId}`, 'success');
    } catch {
      showToast('No se pudo escalar a no conformidad.', 'error');
    } finally {
      setEscalatingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <header className="space-y-2 border-b border-border pb-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Mejora continua · cl. 10.3
        </p>
        <h1 className="text-2xl font-semibold tracking-tight">Sugerencias de mejora</h1>
        <p className="text-sm text-muted-foreground">
          Canal para propuestas del personal. El escalado a no conformidad es una acción explícita del responsable.
        </p>
      </header>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {canEdit && (
        <section className="rounded-lg border border-border bg-card p-4 space-y-3">
          <h2 className="font-medium">Nueva sugerencia</h2>
          <input
            className="input w-full"
            placeholder="Título"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
          />
          <textarea
            className="input w-full min-h-[100px]"
            placeholder="Descripción de la mejora propuesta"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={10000}
          />
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
            disabled={saving || !title.trim() || !description.trim()}
            onClick={() => void handleCreate()}
          >
            {saving ? 'Guardando…' : 'Enviar sugerencia'}
          </button>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="font-medium">Registro</h2>
        {loading ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin sugerencias registradas.</p>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => {
              const selectableStatuses = getSelectableStatuses(item.status);
              const isFinalStatus = selectableStatuses.length === 1;

              return (
                <li key={item.id} className="rounded-lg border border-border bg-card p-4 space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground">{item.code}</p>
                      <p className="font-medium">{item.title}</p>
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                        {item.description}
                      </p>
                    </div>
                    <span className="rounded px-2 py-0.5 text-xs bg-muted">
                      {STATUS_LABEL[item.status]}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enviada por {item.submittedBy?.email ?? `usuario #${item.submittedByUserId}`} ·{' '}
                    <DateCell value={item.createdAt} />
                  </p>
                  {item.bridgeRecordId && (
                    <p className="text-xs">
                      NC vinculada:{' '}
                      <Link to={`/registros/no-conformidades/${item.bridgeRecordId}`} className="text-primary hover:underline">
                        {item.bridgeRecordId}
                      </Link>
                    </p>
                  )}
                  {canEdit && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      <select
                        className="input text-sm disabled:cursor-not-allowed disabled:opacity-70"
                        value={item.status}
                        disabled={isFinalStatus}
                        aria-readonly={isFinalStatus}
                        onChange={(e) =>
                          void handleStatusChange(
                            item.id,
                            e.target.value as ImprovementSuggestionStatus,
                          )
                        }
                      >
                        {selectableStatuses.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABEL[s]}
                          </option>
                        ))}
                      </select>
                      {!item.bridgeRecordId && (
                        <button
                          type="button"
                          className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
                          disabled={escalatingId === item.id}
                          onClick={() => void handleEscalate(item.id)}
                        >
                          {escalatingId === item.id ? 'Escalando…' : 'Escalar a NC'}
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
